/**
 * @swagger http://rests.afisha.ru/rests/api/public/v1/swagger/
 */

import { apiPublicMock } from './apiPublicMock'

import * as Types from '@shared/api/types/apiPublicParameters'
import parseCookie from '@shared/lib/helpers/parseCookie'
import { apiRequest } from '@shared/api/apiRequest'
import { mappingAPI, MAPPING_API_GQL } from '@shared/api/middleware/mappingAPI'
import { middlewareError, textErrorsApiPublic } from '@shared/api/middleware/middlewareError'
import { LightGQL } from '@shared/api/gql-service/LightGQLService'
import { GQL_TAGS } from '@shared/api/api.gql'
import { globalStore, RootState } from '@app/model/store'

// const BASE_DOMAIN = 'https://artemis.dev.rests.afisha.ru'
const BASE_DOMAIN = process.env.IS_STAGE === 'true' ? 'https://artemis.dev.rests.afisha.ru' : 'https://www.afisha.ru'
const BASE_URL = `${BASE_DOMAIN}/rests/api/public/v1`
const BOOKING_TOKEN = 'BookingPartnerToken 072b6f56b28f9a8c600775143f9f923779406256' // TODO: to .env
const GITLAB_STATUS = process.env.GITLAB_STATUS
const IS_PROD = GITLAB_STATUS === '0'

// TODO: IS_TEMP_FIX - временное ограничение вывода ошибки, необходимо сделать так,
// чтобы она не пыталась отобразиться если пользователь еще не авторизован,
// например можно проверять наличие cookie, через какой-нибудь middleware, и только потом делать запрос user/me
const IS_TEMP_FIX = false

export const API_PUBLIC = {
  // ASSISTANT
  assistant_list: async (props: Types.PropsAssistantList) => {
    try {
      return await mappingAPI.assistant_list(
        apiRequest.get({
          url: `${BASE_URL}/assistant/`,
          params: {
            page: props?.params?.page || 1,
            page_size: props?.params?.page_size || 3,
          },
          ...props,
        }),
      )
    } catch (error) {
      middlewareError('assistant_list', props)
      throw error
    }
  },

  assistant_create: async (props: Types.PropsAssistantCreate) => {
    const textError = props?.error?.text || textErrorsApiPublic['assistant_create']
    try {
      return await mappingAPI.assistant_create(
        apiRequest.post({
          url: `${BASE_URL}/assistant/`,
          body: {
            question: props.body.question,
          },
          ...props,
        }),
      )
    } catch (error) {
      const serveErrorMsg = error.message
      middlewareError('assistant_create', props, { textError: serveErrorMsg || textError })
      throw error
    }
  },

  // BOOKING
  booking_create: async (props: Types.PropsBookingCreate) => {
    return mappingAPI
      .booking_create(
        apiRequest.post({
          url: `${BASE_URL}/booking/`,
          headers: {
            Authorization: BOOKING_TOKEN,
          },
          ...props,
        }),
      )
      .catch(() => middlewareError('booking_create', props))
  },

  booking_get_slots: (props: Types.PropsGetSlots, flags?: { isRestoplace: boolean; isRemarked: boolean }) => {
    // проверяем статус ресторана из глобального состояния
    const state = globalStore.getState() as RootState
    const isRestoplace = flags?.isRestoplace || state.booking?.restaurantSource?.booking?.afisha?.restoplace?.active
    const isRemarked = flags?.isRemarked || state.booking?.restaurantSource?.booking?.afisha?.remarked?.active

    const targetRest = props.rest

    if (!targetRest) {
      return Promise.reject(new Error('Restaurant slug is required'))
    }

    const queryParams = `?num_of_places=${props.numOfPlaces}&date=${props.date}`

    if (isRestoplace) {
      return mappingAPI
        .booking_get_slots_restoplace(
          apiRequest.get({ url: `${BASE_URL}/restaurant/${targetRest}/slots/${queryParams}`, ...props }),
        )
        .catch(() => middlewareError('booking_get_slots', props))
    } else if (isRemarked) {
      return mappingAPI
        .booking_get_slots_remarked(
          apiRequest.get({ url: `${BASE_URL}/restaurant/${targetRest}/slots/${queryParams}`, ...props }),
        )
        .catch(() => middlewareError('booking_get_slots', props))
    } else return {}
  },

  booking_discount: (props: Types.PropsBookingDiscount) => {
    return mappingAPI
      .booking_discount(apiRequest.post({ url: `${BASE_URL}/booking/discount/`, ...props }))
      .catch(() => middlewareError('booking_discount', props))
  },

  booking_restaurants_list: (props?: Types.PropsBookingRestaurantsList) => {
    return mappingAPI
      .booking_restaurants_list(apiRequest.get({ url: `${BASE_URL}/booking_restaurants/`, ...props }))
      .catch(() => middlewareError('booking_restaurants_list', props))
  },

  // CITY
  city_list: (props?: Types.PropsCityList) => {
    const params = {
      params: {
        page: props?.params?.page || 1,
        page_size: props?.params?.page_size || 1000,
      },
    }

    return mappingAPI
      .city_list(apiRequest.get({ url: `${BASE_URL}/city/`, ...props, ...params }))
      .catch(() => middlewareError('city_list', props))
  },

  city_read: (props: Types.PropsCityRead) => {
    const targetCitySlug = props.path || 'msk'
    return mappingAPI
      .city_read(apiRequest.get({ url: `${BASE_URL}/city/${targetCitySlug}/`, ...props }))
      .catch(() => middlewareError('city_read', props))
  },

  // METRO
  metro_list: (props?: Types.PropsMetroList) => {
    return mappingAPI
      .metro_list(apiRequest.get({ url: `${BASE_URL}/metro/`, ...props }))
      .catch(() => middlewareError('metro_list', props))
  },

  // NEWS
  news_list: (props?: Types.PropsNewsList) => {
    return mappingAPI
      .news_list(apiRequest.get({ url: `${BASE_URL}/news/`, ...props }))
      .catch(() => middlewareError('news_list', props))
  },

  news_read: (props: Types.PropsNewsRead) => {
    return mappingAPI
      .news_read(apiRequest.get({ url: `${BASE_URL}/news/${props.path}/`, ...props }))
      .catch(() => middlewareError('news_read', props))
  },

  // PHOTO
  photo_list: (props: Types.PropsPhotoList) => {
    return mappingAPI
      .photo_list(apiRequest.get({ url: `${BASE_URL}/photo/`, ...props }))
      .catch(() => middlewareError('photo_list', props))
  },

  // RESTAURANT
  restaurant_list: async (props?: Types.PropsRestaurantList) => {
    return mappingAPI
      .restaurant_list(apiRequest.get({ url: `${BASE_URL}/restaurant/`, ...props }))
      .catch(() => middlewareError('restaurant_list', props))
  },

  restaurant_read: (props: Types.PropsRestaurantRead) => {
    return mappingAPI
      .restaurant_read(apiRequest.get({ url: `${BASE_URL}/restaurant/${props.path}/`, ...props }))
      .catch(() => middlewareError('restaurant_read', props))
  },

  restaurant_nearest_rests: (props: Types.PropsRestaurantNearestRests) => {
    return mappingAPI
      .restaurant_nearest_rests(
        apiRequest.get({ url: `${BASE_URL}/restaurant/${props.path}/nearest_rests/`, ...props }),
      )
      .catch(() => middlewareError('restaurant_nearest_rests', props))
  },

  restaurant_popular_rests: (props: Types.PropsRestaurantPopularRests) => {
    return mappingAPI
      .restaurant_popular_rests(
        apiRequest.get({ url: `${BASE_URL}/restaurant/${props.path}/popular_rests/`, ...props }),
      )
      .catch(() => middlewareError('restaurant_popular_rests', props))
  },

  restaurant_similar_rests: (props: Types.PropsRestaurantSimilarRests) => {
    return mappingAPI
      .restaurant_similar_rests(
        apiRequest.get({ url: `${BASE_URL}/restaurant/${props.path}/similar_rests/`, ...props }),
      )
      .catch(() => middlewareError('restaurant_similar_rests', props))
  },

  // REVIEWS
  reviews_list: (props?: Types.PropsReviewsList) => {
    return mappingAPI
      .reviews_list(apiRequest.get({ url: `${BASE_URL}/reviews/`, ...props }))
      .catch(() => middlewareError('reviews_list', props))
  },

  reviews_create: (props: Types.PropsReviewsCreate) => {
    return mappingAPI
      .reviews_create(
        apiRequest.post({
          url: `${BASE_URL}/reviews/`,
          ...props,
        }),
      )
      .catch(() => middlewareError('reviews_create', props))
  },

  reviews_partial_update: (props: Types.PropsReviewsPartialUpdate) => {
    return mappingAPI
      .reviews_partial_update(apiRequest.patch({ url: `${BASE_URL}/reviews/${props.path}/`, ...props }))
      .catch(() => middlewareError('reviews_partial_update', props))
  },

  reviews_update: (props: Types.PropsReviewsUpdate) => {
    return mappingAPI
      .reviews_update(apiRequest.put({ url: `${BASE_URL}/reviews/${props.path}/`, ...props }))
      .catch(() => middlewareError('reviews_update', props))
  },

  reviews_delete: (props: Types.PropsReviewsDelete) => {
    return mappingAPI
      .reviews_delete(apiRequest.delete({ url: `${BASE_URL}/reviews/${props.path}/`, ...props }))
      .catch(() => middlewareError('reviews_delete', props))
  },

  // SELECTION
  selection_list: (props?: Types.PropsSelectionList) => {
    return mappingAPI
      .selection_list(apiRequest.get({ url: `${BASE_URL}/selection/`, ...props }))
      .catch(() => middlewareError('selection_list', props))
  },

  selection_read: (props: Types.PropsSelectionRead) => {
    return mappingAPI
      .selection_read(apiRequest.get({ url: `${BASE_URL}/selection/${props.path}/`, ...props }))
      .catch(() => middlewareError('selection_read', props))
  },

  selection_restaurants: (props: Types.PropsSelectionRestaurants) => {
    return mappingAPI
      .selection_restaurants(apiRequest.get({ url: `${BASE_URL}/selection/${props.path}/restaurants/`, ...props }))
      .catch(() => middlewareError('selection_restaurants', props))
  },

  // RESTORATOR
  restorator: (props: Types.PropsRestoratorPost) => {
    return mappingAPI
      .restorator(
        apiRequest.post({
          url: `${BASE_URL}/restorator/`,
          headers: {
            'X-CSRFToken': parseCookie('csrftoken'),
          },
          ...props,
        }),
      )
      .catch(() => middlewareError('restorator', props))
  },

  restorator_organization: (id: string, props: Types.PropsRestoratorOrgPatch) => {
    return mappingAPI
      .restorator_organization(
        apiRequest.patch({
          url: `${BASE_URL}/restorator/${id}/organization/`,
          ...props,
        }),
      )
      .catch(() => middlewareError('restorator_organization', props))
  },

  restorator_promotion: (id: string, props: Types.PropsRestoratorPromoPatch) => {
    return mappingAPI
      .restorator_promotion(
        apiRequest.patch({
          url: `${BASE_URL}/restorator/${id}/promotion/`,
          ...props,
        }),
      )
      .catch(() => middlewareError('restorator_promotion', props))
  },

  // SEO
  seo_read: (props: Types.PropsSeoRead) => {
    return mappingAPI
      .seo_read(apiRequest.get({ url: `${BASE_URL}/seo/${props.path}`, ...props }))
      .catch(() => middlewareError('seo_read', props))
  },

  // TAG
  tag_list: (props?: Types.PropsTagList) => {
    return mappingAPI
      .tag_list(apiRequest.get({ url: `${BASE_URL}/tag/`, ...props }))
      .catch(() => middlewareError('tag_list', props))
  },

  // USER
  user_logout: (props: Types.PropsUserLogout) => {
    const thisMock = apiPublicMock['user_logout']
    const AUTH_PERM_ENABLED = true

    return mappingAPI
      .user_logout(
        AUTH_PERM_ENABLED || IS_PROD
          ? apiRequest.post({ url: `${BASE_URL}/user/logout/`, ...props })
          : new Promise((resolve) => {
              resolve(thisMock)
            }),
      )
      .catch(() => middlewareError('user_logout', props))
  },

  user_me: async (props?: Types.PropsUserMe) => {
    const showErrorUser = IS_TEMP_FIX && (props?.error?.showUser || false)
    const thisMock = apiPublicMock['user_me']

    const prodRequest = () =>
      apiRequest.get({ url: `${BASE_URL}/user/me/`, ...props }).then(async (res) => {
        const variables = { id: `User_${res.afisha_id}`, first: 999 }

        const resultGraph = await LightGQL.afisha.query(GQL_TAGS.FAVORITES, {
          variables,
          mapping: MAPPING_API_GQL.favorites,
          requestInit: { credentials: 'include' },
        })

        return { ...res, favoritesGraph: resultGraph }
      })

    const devRequest = () =>
      LightGQL.afisha
        .query(GQL_TAGS.FAVORITES, {
          variables: { id: 'User_5931952', first: 999 },
          mapping: MAPPING_API_GQL.favorites,
          requestInit: { credentials: 'include' },
        })
        .then((resultGraph) => {
          return { ...thisMock, favoritesGraph: resultGraph }
        })
        .catch(() => ({ ...thisMock }))

    const AUTH_PERM_ENABLED = true

    return mappingAPI
      .user_me(AUTH_PERM_ENABLED ? prodRequest() : devRequest())
      .catch(() => middlewareError('user_me', props, { isShowToUser: showErrorUser, isThrowException: false }))
  },

  // VARIETY
  variety_list: (props?: Types.PropsVarietyList) => {
    return mappingAPI
      .variety_list(apiRequest.get({ url: `${BASE_URL}/variety/`, ...props }))
      .catch(() => middlewareError('variety_list', props))
  },

  // VIDEO
  video_list: (props?: Types.PropsVideoList) => {
    return mappingAPI
      .video_list(apiRequest.get({ url: `${BASE_URL}/video/`, ...props }))
      .catch(() => middlewareError('video_list', props))
  },

  // COMPLAIN
  /**
   * @function send_mistake - отправить обращение по ошибке
   */
  send_mistake: async (props: Types.PropsSendMistake) => {
    return mappingAPI
      .send_mistake(
        apiRequest.post({
          url: `${BASE_URL}/complain/`,
          headers: {
            Authorization: BOOKING_TOKEN,
          },
          ...props,
        }),
      )
      .catch(() => middlewareError('send_mistake', props))
  },

  /**
   * @function send_new_rest - отправить форму для нового ресторана
   */
  send_new_rest: async (props: Types.PropsSendNewRest) => {
    return apiRequest
      .post({
        url: `${BASE_URL}/complain/`,
        headers: {
          'X-CSRFToken': parseCookie('csrftoken'),
        },
        ...props,
      })
      .catch(() => middlewareError('send_new_rest', props))
  },
  // END
}
